import { RegisterButtonLocation, RegisterFunctions } from "@repo/database";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import type { Button } from "~/providers/button-provider";
import { useBookingStore } from "~/stores/use-booking-store";
import { useOrderStore } from "~/stores/use-order-store";
import { useBookingManager } from "./use-booking-manager";
import { useCartManager } from "./use-cart-manager";
import { useOrderManager } from "./use-order-manager";
import { usePermissionManager } from "./use-permission-manager";
import { useRegisterManager } from "./use-register-manager";

export const useFunctionManager = () => {
  const { currentOrder, isLoading } = useOrderStore();

  const {
    deleteOrder,
    updateOrder,
    nextRound,
    initializeCheckout,
    openOpenOrders,
    openOrderTransfer,
    openSplitOrder,
  } = useOrderManager();

  const { logout } = usePermissionManager();

  const { openBookingDialog } = useBookingManager();

  const { openDayOverview, openDrawer, openDrawerMutation, openEndDay } =
    useRegisterManager();

  const {
    openMiscProduct,
    openOrderComment,
    openOrderDiscount,
    openOrderContext,
    cancelOperation,
  } = useCartManager();

  const { setSelectedView } = useBookingStore(
    useShallow((state) => ({
      setSelectedView: state.setSelectedView,
    })),
  );

  const navigate = useNavigate();

  const { t } = useTranslation("common");

  const isDisabled = (button?: Button) => {
    if (!button) return false;
    switch (button.function) {
      case RegisterFunctions.split_order:
        return currentOrder?.items && currentOrder.items.length === 0
          ? true
          : Boolean(isLoading);
      case RegisterFunctions.table_next_round:
        return !currentOrder ? true : Boolean(isLoading);
      case RegisterFunctions.delete_order:
        return currentOrder?.items && currentOrder.items.length === 0
          ? true
          : Boolean(isLoading);
      case RegisterFunctions.misc_product:
        return Boolean(isLoading);
      case RegisterFunctions.order:
        return Boolean(isLoading);
      case RegisterFunctions.checkout:
        return Boolean(isLoading || currentOrder?.id === undefined);
      case RegisterFunctions.transfer_table:
        return Boolean(isLoading);
      case RegisterFunctions.print_preliminary_receipt:
        return Boolean(isLoading);
      default:
        return false;
    }
  };

  const getDefaultColor = (registerFunction?: RegisterFunctions) => {
    if (!registerFunction) return undefined;

    switch (registerFunction) {
      case RegisterFunctions.split_order:
        return "#c55d00";

      case RegisterFunctions.end_day:
        return "";
      case RegisterFunctions.guests:
        return "";
      case RegisterFunctions.tables:
        return "";
      case RegisterFunctions.view_open_orders:
        return "";
      case RegisterFunctions.order:
        return "#007AFF";
      case RegisterFunctions.misc_product:
        return "#04abb7";
      case RegisterFunctions.table_next_round:
        return "#00fff2";
      case RegisterFunctions.table_overview:
        return "#7e066c";
      case RegisterFunctions.calendar:
        return "";
      case RegisterFunctions.print_preliminary_receipt:
        return "#f3eb00";
      case RegisterFunctions.checkout:
      case RegisterFunctions.new_booking:
        return "#006703";
      case RegisterFunctions.day_overview:
        return "#007AFF";
      case RegisterFunctions.transfer_table:
        return "#000000";
      case RegisterFunctions.order_comment:
        return "";
      case RegisterFunctions.order_discount:
        return "#ffcb64";
      case RegisterFunctions.open_drawer:
        return "#e5a700";
      case RegisterFunctions.logout:
      case RegisterFunctions.delete_order:
      case RegisterFunctions.cancel_operation:
        return "#ff0000";

      default:
        return "";
    }
  };

  const getFunction = (registerFunction?: RegisterFunctions) => {
    if (!registerFunction) return undefined;

    switch (registerFunction) {
      case RegisterFunctions.end_day:
        return openEndDay;
      case RegisterFunctions.logout:
        return logout;
      case RegisterFunctions.split_order:
        return openSplitOrder;
      case RegisterFunctions.drawer_mutation:
        void openDrawerMutation();
        return;
      case RegisterFunctions.void_order:
        return undefined;
      case RegisterFunctions.guests:
        return () => {
          setSelectedView("guests");
          void navigate({ to: "/bookings" });
        };
      case RegisterFunctions.tables:
        return () => {
          setSelectedView("tables");
          void navigate({ to: "/bookings" });
        };
      case RegisterFunctions.view_open_orders:
        return openOpenOrders;
      case RegisterFunctions.order:
        return () => {
          void updateOrder({
            printTicket: true,
            printAll: false,
            closeAfterUpdate: true,
          });
        };
      case RegisterFunctions.misc_product:
        return () => {
          void openMiscProduct();
        };
      case RegisterFunctions.new_booking:
        return () => {
          void openBookingDialog();
        };
      case RegisterFunctions.table_next_round:
        return () => {
          if (!currentOrder) return;
          void nextRound(currentOrder);
        };
      case RegisterFunctions.table_overview:
        return () => {
          void navigate({ to: "/floor-plan" });
        };
      case RegisterFunctions.calendar:
        return () => {
          void navigate({ to: "/bookings/guest-planning" });
        };
      case RegisterFunctions.print_preliminary_receipt:
        return async () => {
          await updateOrder({
            printTicket: true,
            printProforma: true,
            printAll: false,
            closeAfterUpdate: true,
          });
        };
      case RegisterFunctions.checkout:
        return () => {
          void initializeCheckout();
        };
      case RegisterFunctions.day_overview:
        return openDayOverview;
      case RegisterFunctions.delete_order:
        return () => {
          if (currentOrder) {
            void deleteOrder(currentOrder.id);
          }
        };
      case RegisterFunctions.transfer_table:
        return () => {
          if (currentOrder) {
            openOrderTransfer(currentOrder);
            void navigate({ to: "/floor-plan" });
          }
        };
      case RegisterFunctions.transfer_items:
        return () => {
          openOrderTransfer();
        };
      case RegisterFunctions.order_comment:
        return () => {
          void openOrderComment();
        };
      case RegisterFunctions.order_discount:
        return () => {
          void openOrderDiscount();
        };
      case RegisterFunctions.open_drawer:
        return () => {
          void openDrawer();
        };
      case RegisterFunctions.cancel_operation:
        return () => {
          cancelOperation();
        };

      default:
        return () => undefined;
    }
  };

  const getAvailableFunctions = (location: RegisterButtonLocation) => {
    switch (location) {
      case RegisterButtonLocation.footer:
      case RegisterButtonLocation.sidebar_right:
        return [
          RegisterFunctions.order,
          RegisterFunctions.logout,
          RegisterFunctions.split_order,
          RegisterFunctions.guests,
          RegisterFunctions.void_order,
          RegisterFunctions.tables,
          RegisterFunctions.transfer_items,
          RegisterFunctions.table_next_round,
          RegisterFunctions.view_open_orders,
          RegisterFunctions.end_day,
          RegisterFunctions.open_drawer,
          RegisterFunctions.delete_order,
          RegisterFunctions.print_preliminary_receipt,
          RegisterFunctions.misc_product,
          RegisterFunctions.day_overview,
          RegisterFunctions.order_discount,
          RegisterFunctions.order_comment,
          RegisterFunctions.table_overview,
          RegisterFunctions.checkout,
          RegisterFunctions.new_booking,
          RegisterFunctions.cancel_operation,
          RegisterFunctions.transfer_table,
          RegisterFunctions.drawer_mutation,
        ];
      case RegisterButtonLocation.calendar_footer:
      case RegisterButtonLocation.calendar_sidebar:
        return [
          RegisterFunctions.logout,
          RegisterFunctions.day_overview,
          RegisterFunctions.end_day,
          RegisterFunctions.drawer_mutation,
          RegisterFunctions.open_drawer,
          RegisterFunctions.table_overview,
          RegisterFunctions.new_booking,
        ];
      case RegisterButtonLocation.floor_plan_footer:
      case RegisterButtonLocation.floor_plan_sidebar:
        return [
          RegisterFunctions.day_overview,
          RegisterFunctions.logout,
          RegisterFunctions.drawer_mutation,
          RegisterFunctions.end_day,
          RegisterFunctions.calendar,
          RegisterFunctions.open_drawer,
          RegisterFunctions.new_booking,
          RegisterFunctions.transfer_table,
        ];
      case RegisterButtonLocation.dashboard_footer:
      case RegisterButtonLocation.dashboard_sidebar:
        return [
          RegisterFunctions.day_overview,
          RegisterFunctions.logout,
          RegisterFunctions.drawer_mutation,
          RegisterFunctions.open_drawer,
          RegisterFunctions.table_overview,
          RegisterFunctions.end_day,
          RegisterFunctions.new_booking,
          RegisterFunctions.calendar,
          RegisterFunctions.tables,
        ];
      default:
        return [];
    }
  };

  const getLabel = (registerFunction?: RegisterFunctions) => {
    if (!registerFunction) return "";

    switch (registerFunction) {
      case RegisterFunctions.close_register:
        return t("end_day");
      case RegisterFunctions.logout:
        return t("logout");
      case RegisterFunctions.split_order:
        return t("split_order");
      case RegisterFunctions.order:
        return t("send_order");
      case RegisterFunctions.guests:
        return t("guests");
      case RegisterFunctions.drawer_mutation:
        return t("drawer_mutation");
      case RegisterFunctions.tables:
        return t("tables");
      case RegisterFunctions.table_next_round:
        return t("table_next_round");
      case RegisterFunctions.view_open_orders:
        return t("view_open_orders");
      case RegisterFunctions.end_day:
        return t("end_day");
      case RegisterFunctions.delete_order:
        return t("delete_order");
      case RegisterFunctions.void_order:
        return t("void_order");
      case RegisterFunctions.misc_product:
        return t("product");
      case RegisterFunctions.day_overview:
        return t("day_overview");
      case RegisterFunctions.order_discount:
        return t("order_discount");
      case RegisterFunctions.order_comment:
        return t("order_comment");
      case RegisterFunctions.table_overview:
        return t("floor_plan");
      case RegisterFunctions.checkout:
        return t("checkout");
      case RegisterFunctions.cancel_operation:
        return t("cancel");
      case RegisterFunctions.print_preliminary_receipt:
        return t("pro_forma_ticket");
      case RegisterFunctions.calendar:
        return t("calendar");
      case RegisterFunctions.open_drawer:
        return t("open_drawer");
      case RegisterFunctions.new_booking:
        return t("new_booking");
      case RegisterFunctions.transfer_table:
        return t("transfer");
      case RegisterFunctions.transfer_items:
        return t("transfer_items");
      default:
        return "";
    }
  };

  const getContext = (registerFunction?: RegisterFunctions) => {
    if (!registerFunction) return undefined;
    switch (registerFunction) {
      case RegisterFunctions.order:
        return () => openOrderContext();
      default:
        return undefined;
    }
  };

  return {
    isDisabled,
    getFunction,
    getLabel,
    getContext,
    getAvailableFunctions,
    getDefaultColor,
  };
};
